import type { ProjectMarketResolutionAtResult } from './resolution-threshold-projection.js';

export interface MarketShareDisplayReductionInput {
  readonly values: ReadonlyArray<number>;
  readonly threshold: number | null;
  readonly forecast: ProjectMarketResolutionAtResult | null;
  readonly displayedHeadTimeMs: number;
}

export interface MarketShareDisplayReduction {
  readonly outcomeOrder: ReadonlyArray<number>;
  readonly leaderOutcomeIndex: number | null;
  readonly thresholdReached: boolean;
  readonly forecastUpdating: boolean;
}

/** Reduces one displayed Market Share head into its discrete domain state. */
export function reduceMarketShareDisplay(
  input: MarketShareDisplayReductionInput,
): MarketShareDisplayReduction {
  const threshold = input.threshold;
  const thresholdReached =
    threshold !== null &&
    input.values.some((value) => Number.isFinite(value) && value >= threshold);

  return {
    outcomeOrder: input.values
      .map((value, outcomeIndex) => ({ value, outcomeIndex }))
      .sort((left, right) => right.value - left.value || left.outcomeIndex - right.outcomeIndex)
      .map(({ outcomeIndex }) => outcomeIndex),
    leaderOutcomeIndex: leaderIndex(input.values),
    thresholdReached,
    forecastUpdating:
      !thresholdReached &&
      input.forecast !== null &&
      input.forecast.crossAtMs <= input.displayedHeadTimeMs,
  };
}

function leaderIndex(values: ReadonlyArray<number>): number | null {
  let leader: number | null = null;
  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (value === undefined || !Number.isFinite(value)) continue;
    if (leader === null || value > (values[leader] ?? Number.NEGATIVE_INFINITY)) leader = index;
  }
  return leader;
}
